import React from 'react';
import './App.scss';
import { Route, Routes } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';

// import Home from './views/Home';
import TimeSheet from './views/TimeSheet';
import { hasAuthParams, useAuth } from 'react-oidc-context';
// import NavBar from './components/Navbar';
import Layout from './views/Layout';
import Dashboard from './views/Dashboard';
import { FullScreenLoader } from './components/FullScreenLoader';
import HasAccess from './components/HasAccess';
import ActivitiesList from './views/ManageActivities/List';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllUsers,
  getUserRoles,
} from 'src/redux/features/users/usersSlice';
import ManageActivities from './views/ManageActivities';
import ProjectAccess from './views/ProjectAccess';
import EditProjectAccess from './views/ProjectAccess/EditProjectAccess/index';
import ManagePos from './views/ManagePos';
import BulkApprovals from './views/BulkApprovals';
import PoReceiving from './views/PoReceivings';
import AddProduct from './views/AddProduct';
import en from './messages/en.json';
import EditEmailNotifications from './views/EditEmailNotifications';

function App(): JSX.Element {
  const auth = useAuth();
  const userRoles = useSelector(getUserRoles);
  const [hasTriedSignin, setHasTriedSignin] = React.useState(false);
  const [isValidUser, setIsValidUser] = React.useState<boolean | null>(null);
  const [isCheckingUser, setIsCheckingUser] = React.useState(false);

  const dispatch = useDispatch();

  // automatically sign-in
  React.useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  const email = auth.user?.profile?.email;

  const validateUser = async () => {
    setIsCheckingUser(true);
    try {
      const res = await dispatch(
        fetchAllUsers({ query: email, internalResources: true }),
      ).unwrap();

      if (
        res &&
        res.length > 0 &&
        email.toLowerCase().endsWith('@daikinapplied.com')
      ) {
        setIsValidUser(true);
      } else {
        setIsValidUser(false);
        setTimeout(() => {
          auth.signinRedirect({ extraQueryParams: { prompt: 'login' } });
        }, 4000);
      }
    } catch (err) {
      if (
        err?.status === 401 ||
        err?.response?.status === 401 ||
        err?.message?.includes('Unauthorized')
      ) {
        auth.signinRedirect();
      } else {
        auth.signinRedirect({ extraQueryParams: { prompt: 'login' } });
      }
    } finally {
      setIsCheckingUser(false);
    }
  };

  React.useEffect(() => {
    if (!email) return;

    validateUser();
  }, [email, dispatch]);

  if (isCheckingUser) {
    return <FullScreenLoader showLoader title={en.Common.Title.loading} />;
  }

  if (isValidUser === false) {
    return (
      <div className="flex items-center justify-center h-screen text-red-600 text-xl">
        {en.Common.Title.nonDaikinUser}
      </div>
    );
  }

  if (auth.isLoading) {
    return <FullScreenLoader showLoader title={en.Common.Title.signingInOut} />;
  }

  if (!auth.isAuthenticated) {
    return (
      <FullScreenLoader
        showLoader={false}
        title={en.Common.Title.unableToLogin}
      />
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route path="" element={<TimeSheet />} />
          <Route path="/timesheet" element={<TimeSheet />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/team-timesheet"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexAdmin', 'CapexAdManager']}
              >
                <TimeSheet teamTime={true} />
              </HasAccess>
            }
          />
          <Route
            path="/bulk-approvals"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexAdManager']}
              >
                <BulkApprovals />
              </HasAccess>
            }
          />
          <Route
            path="/manage-activities"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={[
                  'CapexAdmin',
                  'CapexProjectLeader',
                  'CapexCategoryManager',
                ]}
              >
                <ManageActivities />
              </HasAccess>
            }
          >
            <Route path="" element={<ActivitiesList isModalOpen={false} />} />
            <Route
              path="list"
              element={<ActivitiesList isModalOpen={false} />}
            />
          </Route>
          <Route
            path="/project-access"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexProjectLeader']}
              >
                <ProjectAccess />
              </HasAccess>
            }
          />
          <Route
            path="/project-access/:id"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexProjectLeader']}
              >
                <EditProjectAccess />
              </HasAccess>
            }
          />
          <Route
            path="/manage-po"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexITLeader']}
              >
                <ManagePos />
              </HasAccess>
            }
          />
          <Route
            path="/po-receiving"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexITLeader']}
              >
                <PoReceiving />
              </HasAccess>
            }
          />
          <Route
            path="/edit-email-notifications"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexAdManager']}
              >
                <EditEmailNotifications />
              </HasAccess>
            }
          />
          <Route
            path="/edit-products"
            element={
              <HasAccess
                userRole={userRoles.data}
                allowedRoles={['CapexAdmin']}
              >
                <AddProduct />
              </HasAccess>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
        </Route>
      </Routes>
    </div>
  );
}

export default App;
